import { type AuthSession } from '@/common/types/auth-session.type';
import { readCookie } from '@/common/utils/auth-cookie.client';
import {
  AUTH_COOKIE_NAME,
  AUTH_USER_ID_COOKIE_NAME,
} from '@/common/utils/auth-cookie.shared';

let cachedSession: AuthSession | null = null;
let inFlight: Promise<AuthSession | null> | null = null;

function isFreshSession(session: AuthSession | null): session is AuthSession {
  return Boolean(session && session.expiresAt > Date.now());
}

export async function fetchAuthSession(
  force = false
): Promise<AuthSession | null> {
  if (!force && isFreshSession(cachedSession)) {
    return cachedSession;
  }

  if (!force && inFlight) {
    return await inFlight;
  }

  const request = (async () => {
    try {
      const raw = readCookie(AUTH_COOKIE_NAME);
      if (raw) {
        const mobile = readCookie(AUTH_USER_ID_COOKIE_NAME)?.trim();
        const session: AuthSession = {
          accessToken: raw,
          expiresAt: Number.MAX_SAFE_INTEGER,
          refreshToken: '',
          tokenType: 'Bearer',
          ...(mobile ? { mobile } : {}),
        };
        cachedSession = session;
        return session;
      }

      if (isFreshSession(cachedSession)) {
        return cachedSession;
      }

      cachedSession = null;
      return null;
    } catch {
      return isFreshSession(cachedSession) ? cachedSession : null;
    }
  })();

  inFlight = request;
  try {
    return await request;
  } finally {
    if (inFlight === request) {
      inFlight = null;
    }
  }
}

export function getCachedAccessToken(): string | null {
  if (isFreshSession(cachedSession)) {
    return cachedSession.accessToken;
  }
  const raw = readCookie(AUTH_COOKIE_NAME);
  if (raw) {
    cachedSession = {
      accessToken: raw,
      refreshToken: '',
      tokenType: 'Bearer',
      expiresAt: Number.MAX_SAFE_INTEGER,
    };
    return raw;
  }
  return null;
}

export function clearCachedSession() {
  cachedSession = null;
}

export function setCachedSession(session: AuthSession) {
  cachedSession = session;
}
